import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Loader from "../../Loader/Loader";
import { orderlist, exportOrdersCsv } from "../../service/Mainservice";
import Pagination from "../Pagination/pagination";
import ListControls from "../ListControls/ListControls";
import ExportCsvButton from "../ExportCsvButton";
import SearchIcon from "@mui/icons-material/Search";
import "./Order.css"; // we'll create a new CSS file
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { IconButton, Tooltip } from "@mui/material";

const STATUS_TABS = [
  ["all", "All"],
  ["pending", "Pending"],
  ["in_progress", "In Progress"],
  ["delivered", "Delivered"],
  ["cancelled", "Cancelled"],
  ["refunded", "Refunded"],
  ["disputed", "Disputed"],
];

const DEFAULT_PAGINATION = {
  currentPage: 1,
  totalPages: 1,
  pageSize: 10,
  totalCount: 0,
};

const OrderList = () => {
  const [loading, setloading] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [searchvalue, setsearchvalue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("all"); // 'all' or a tab key
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [orderDeliveryType, setOrderDeliveryType] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const navigate = useNavigate();

  const activeFilterCount = [
    activeStatus !== "all",
    dateFilter,
    dateFrom,
    dateTo,
    paymentMethod,
    orderDeliveryType,
    deliveryType,
    minAmount,
    maxAmount,
    pageSize !== 10,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setActiveStatus("all");
    setDateFilter("");
    setDateFrom("");
    setDateTo("");
    setPaymentMethod("");
    setOrderDeliveryType("");
    setDeliveryType("");
    setMinAmount("");
    setMaxAmount("");
    setPageSize(10);
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchvalue.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchvalue]);

  useEffect(() => {
    getData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    activeStatus,
    dateFrom,
    dateTo,
    dateFilter,
    pageSize,
    sortBy,
    sortOrder,
    paymentMethod,
    orderDeliveryType,
    deliveryType,
    minAmount,
    maxAmount,
  ]);

  const getData = async (page) => {
    try {
      setloading(true);
      const response = await orderlist({
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: debouncedSearch,
        status: activeStatus === "all" ? "" : activeStatus,
        dateFilter,
        startDate: dateFilter ? "" : dateFrom,
        endDate: dateFilter ? "" : dateTo,
        paymentMethod,
        OrderDeliveryType: orderDeliveryType,
        deliveryType,
        minAmount: minAmount === "" ? "" : Number(minAmount),
        maxAmount: maxAmount === "" ? "" : Number(maxAmount),
      });
      const responseData = response?.data || {};
      setData(Array.isArray(responseData.data) ? responseData.data : []);
      const apiPagination = responseData.pagination || {};
      setPagination({
        currentPage: Number(
          apiPagination.currentPage || apiPagination.page || page,
        ),
        totalPages: Number(apiPagination.totalPages || 1),
        pageSize: Number(
          apiPagination.pageSize || apiPagination.limit || pageSize,
        ),
        totalCount: Number(
          apiPagination.totalCount || apiPagination.total || 0,
        ),
      });
      setloading(false);
    } catch (error) {
      setloading(false);
      if (error?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  // Helper to get status badge class
  const getStatusBadge = (status) => {
    const map = {
      Delivered: "status_delivered",
      Pending: "status_pending",
      Confirm: "status_confirm",
      Onway: "status_onway",
      Pickedup: "status_pickedup",
      Cancelled: "status_cancelled",
      Refunded: "status_refunded",
      Disputed: "status_disputed",
    };
    return map[status] || "status_default";
  };

  const handleRowClick = (id) => {
    navigate("/Order_Details", {
      state: { id: id },
    });
  };

  return (
    <>
      <div className="container1">
        <div className="data_head">
          <h1 className="data_heading">All Orders</h1>
          <div className="searchicon">
            <SearchIcon />
            <input
              type="search"
              placeholder="Search by order #, customer, store, driver..."
              onChange={(e) => setsearchvalue(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <ListControls
          sortOptions={[
            { value: "createdAt", label: "Created date" },
            { value: "updatedAt", label: "Updated date" },
            { value: "totalPrice", label: "Order total" },
            { value: "orderNumber", label: "Order number" },
            { value: "orderStatus", label: "Status" },
            { value: "customer.fullName", label: "Customer" },
            { value: "vendor.shopName", label: "Store" },
            { value: "deliveryPerson.fullName", label: "Driver" },
          ]}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortByChange={setSortBy}
          onSortOrderChange={setSortOrder}
          activeFilterCount={activeFilterCount}
          onClearFilters={clearFilters}
        >
          <div className="filter_group">
            <label>Status</label>
            <select value={activeStatus} onChange={(e) => setActiveStatus(e.target.value)}>
              {STATUS_TABS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="filter_group">
            <label>Quick Date</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="">Custom / All</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
            </select>
          </div>
          <div className="filter_group">
            <label>Date From</label>
            <input
              type="date"
              value={dateFrom}
              disabled={Boolean(dateFilter)}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="filter_group">
            <label>Date To</label>
            <input
              type="date"
              value={dateTo}
              disabled={Boolean(dateFilter)}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="filter_group">
            <label>Per Page</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="filter_group">
            <label>Payment</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="">All</option>
              <option value="card">Card</option>
              <option value="stripe">Stripe</option>
              <option value="cash">Cash</option>
              <option value="wallet">Wallet</option>
            </select>
          </div>
          <div className="filter_group">
            <label>Fulfilment</label>
            <select
              value={orderDeliveryType}
              onChange={(e) => setOrderDeliveryType(e.target.value)}
            >
              <option value="">All</option>
              <option value="StorePickup">Store Pickup</option>
              <option value="HomeDelivery">Home Delivery</option>
            </select>
          </div>
          <div className="filter_group">
            <label>Delivery Model</label>
            <select
              value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
            >
              <option value="">All</option>
              <option value="SelfDelivery">Self Delivery</option>
              <option value="deliveryBoy">Delivery Boy</option>
            </select>
          </div>
          <div className="filter_group amount_filter">
            <label>Min Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
            />
          </div>
          <div className="filter_group amount_filter">
            <label>Max Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
            />
          </div>
        </ListControls>

        <ExportCsvButton
          exportRequest={exportOrdersCsv}
          filters={{
            search: debouncedSearch,
            status: activeStatus === "all" ? "" : activeStatus,
            dateFilter,
            startDate: dateFilter ? "" : dateFrom,
            endDate: dateFilter ? "" : dateTo,
            paymentMethod,
            OrderDeliveryType: orderDeliveryType,
            deliveryType,
            minAmount: minAmount === "" ? "" : Number(minAmount),
            maxAmount: maxAmount === "" ? "" : Number(maxAmount),
            sortBy,
            sortOrder,
          }}
          filename="orders.csv"
        />

        {data.length > 0 ? (
          <>
            <div className="data_table_over">
              <table className="data_table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Store</th>
                    <th>Driver</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() =>
                        navigate("/Order_Details", {
                          state: { id: item.id },
                        })
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        {item.orderNumber || item.id?.slice(0, 8) || "N/A"}
                      </td>
                      <td>
                        {item.customer?.fullName || "N/A"}
                        <div className="sub_text">
                          {item.customer?.email || ""}
                        </div>
                      </td>
                      <td>
                        {item.vendor?.shopName || "N/A"}
                        <div className="sub_text">
                          {item.vendor?.fullName || ""}
                        </div>
                      </td>
                      <td>{item.deliveryPerson?.fullName || "Not Assigned"}</td>
                      <td>${Number(item.totalPrice || 0).toFixed(2)}</td>
                      <td>
                        <span
                          className={`status_badge ${getStatusBadge(item.orderStatus)}`}
                        >
                          {item.orderStatus}
                        </span>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action_buttons">
                          <Tooltip title="View Details">
                            <IconButton>
                              <RemoveRedEyeIcon
                                sx={{
                                  color: "#3b82f6",
                                  fontSize: "25px",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(item.id);
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.totalPages > 1 && (
              <Pagination pagedata={pagination} onPageChange={getData} />
            )}
          </>
        ) : (
          <p className="nodata">No orders found matching the filters.</p>
        )}
      </div>

      {loading && <Loader />}
    </>
  );
};

export default OrderList;
